import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ConfigStateService } from '@abp/ng.core';
import { catchError, map, of, switchMap, take } from 'rxjs';

/**
 * Guard que valida que el usuario tenga rol de administrador.
 *
 * winepassport#347 (WP-001): antes leía `currentUser` del `ConfigStateService` cacheado, que
 * podía quedar con roles viejos después de que un admin cambiara el rol de la cuenta (la
 * sesión seguía "siendo admin" en el SPA). Ahora fuerza `refreshAppState()` — que re-consulta
 * `/api/abp/application-configuration` con el token actual — antes de evaluar el rol.
 *
 * Comportamiento:
 * - Refresh fallido (token revocado por SecurityStamp, red caída) -> login. NO se cae al estado
 *   cacheado: sería la misma ventana de rol viejo que este guard existe para cerrar.
 * - Sin usuario / no autenticado -> login
 * - Autenticado pero no admin -> home
 * - Admin -> acceso
 */
export const adminGuard: CanActivateFn = () => {
  const config = inject(ConfigStateService);
  const router = inject(Router);

  return config.refreshAppState().pipe(
    take(1),
    switchMap(() => config.getOne$('currentUser').pipe(take(1))),
    catchError(() => of(null)),
    map(user => {
      if (!user || !user.isAuthenticated) {
        router.navigate(['/account/login']);
        return false;
      }

      if (!(user.roles ?? []).includes('admin')) {
        router.navigate(['/']);
        return false;
      }

      return true;
    }),
  );
};
